import type {
  ChangePaymentMethodData,
  CreatedPrestationReglements,
  PrestationReglementsData,
  PrestationTransactionData,
  PrestationTransactionLabelData,
  SettleCreancesData,
  SubsidyConsumeRefusal,
} from '@cpce/shared';
import { employeesQueryKeys } from '../employees/employees.query-keys';
import { commiteeMutation, MutationMethod } from '../mutations.utils';
import { paymentsQueryKeys } from '../payments/payments.query-keys';
import { prestationsQueryKeys } from '../prestations/prestations.query-keys';
import {
  ECRITURES_REWRITE_INVALIDATIONS,
  transactionsQueryKeys,
} from '../transactions/transactions.query-keys';
import { prestationTransactionsQueryKeys } from './prestation-transactions.query-keys';

export function useUpdatePrestationTransaction(id: number) {
  return commiteeMutation<
    { subsidyRefusals: SubsidyConsumeRefusal[] },
    { transaction: PrestationTransactionData },
    PrestationTransactionData
  >({
    url: `/commitee/:commiteeId/prestation-transactions/${id}`,
    method: MutationMethod.PUT,
    // The écriture carries the instruments Treasury lists, so « Règlements à remettre » goes stale
    // with it.
    invalidateQueries: [prestationTransactionsQueryKeys.all, paymentsQueryKeys.all],
    body: (data: PrestationTransactionData) => ({
      transaction: data,
    }),
  });
}

// The écriture is left alone: only the instruments are renamed, which is what makes this reachable
// on a règlement the full update refuses.
export function useUpdatePrestationTransactionLabel(id: number) {
  return commiteeMutation<
    unknown,
    PrestationTransactionLabelData,
    PrestationTransactionLabelData['label']
  >({
    url: `/commitee/:commiteeId/prestation-transactions/${id}/label`,
    method: MutationMethod.PATCH,
    invalidateQueries: [prestationTransactionsQueryKeys.all, paymentsQueryKeys.all],
    body: (label) => ({ label }),
    successMessage: 'Intitulé mis à jour',
  });
}

export function useChangePaymentMethod(id: number) {
  return commiteeMutation<void, ChangePaymentMethodData, ChangePaymentMethodData>({
    url: `/commitee/:commiteeId/prestation-transactions/${id}/payment-method`,
    method: MutationMethod.PUT,
    invalidateQueries: [
      prestationTransactionsQueryKeys.all,
      paymentsQueryKeys.all,
      ...ECRITURES_REWRITE_INVALIDATIONS,
    ],
    body: (data) => data,
    successMessage: 'Mode de règlement modifié',
  });
}

export function useCreatePrestationReglements() {
  return commiteeMutation<
    CreatedPrestationReglements,
    { transaction: PrestationReglementsData },
    PrestationReglementsData
  >({
    url: '/commitee/:commiteeId/prestation-transactions/reglements',
    method: MutationMethod.POST,
    invalidateQueries: [prestationTransactionsQueryKeys.all, paymentsQueryKeys.all],
    body: (data) => ({
      transaction: data,
    }),
  });
}

export function useCancelPrestationTransaction(id: number) {
  return commiteeMutation({
    url: `/commitee/:commiteeId/prestation-transactions/${id}/cancel`,
    method: MutationMethod.POST,
    invalidateQueries: [
      prestationTransactionsQueryKeys.all,
      paymentsQueryKeys.all,
      prestationsQueryKeys.all,
      employeesQueryKeys.all,
      transactionsQueryKeys.all,
    ],
    successMessage: 'Règlement annulé : une contre-passation a été enregistrée',
  });
}

export function useSettleCreances() {
  return commiteeMutation<{ count: number }, SettleCreancesData, SettleCreancesData>({
    url: '/commitee/:commiteeId/prestation-transactions/creances',
    method: MutationMethod.POST,
    invalidateQueries: [
      prestationTransactionsQueryKeys.all,
      paymentsQueryKeys.all,
      prestationsQueryKeys.all,
      employeesQueryKeys.all,
      transactionsQueryKeys.all,
    ],
    body: (data) => data,
    successMessage: ({ participationIds }) =>
      participationIds.length > 1
        ? `${participationIds.length} créances réglées`
        : 'Créance réglée',
  });
}
